import { iCollection } from "@/services/collection";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import { useSectionTitle } from "@/context/SectionTitleContext";
import TaksDatatable from "./TaksDatatable";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "@/components/ui/popover";
import { Info } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import BuybacksTab from "../buybacks/BuybacksTab";
import SaleEventsTab from "../sale-events/SaleEventsTab";
import {
  getAnalysisMaskData,
  MaskDataResponse,
} from "@/services/dataanalysismask";
import { Skeleton } from "@/components/ui/skeleton";
import Overview from "../analytics/Overview";
import { useLocation, useNavigate } from "react-router-dom";
import { useModal } from "@/context/ModalContext";
import CompletionMessage from "../onboarding/CompletionMessage";
import { Button } from "@/components/ui/button";
import dairectBannerBackground from "@/assets/img/dairect-banner-background.svg";
import dairectBannerAiIcon from "@/assets/img/dairect-banner-ai-icon.svg";
import dairectBannerVisibilityIcon from "@/assets/img/dairect-banner-visibility-icon.svg";
import dairectBannerEcosystemIcon from "@/assets/img/dairect-banner-ecosystem-icon.svg";
import dairectBannerArrow from "@/assets/img/dairect-banner-arrow.svg";
import dairectBannerDecoration from "@/assets/img/dairect-banner-decoration.svg";
import { Separator } from "@/components/ui/separator";
import { useSidebar } from "@/components/ui/sidebar";
import { cn } from "@/lib/utils";

const DAIRECT_BANNER_URL = "https://dairect.io/";

function DairectBanner({ isConstrained }: { isConstrained: boolean }) {
  const { t } = useTranslation();

  const cards = [
    {
      icon: dairectBannerAiIcon,
      title: t("collection.dairect_banner.cards.ai_features"),
    },
    {
      icon: dairectBannerVisibilityIcon,
      title: t("collection.dairect_banner.cards.direct_visibility"),
    },
    {
      icon: dairectBannerEcosystemIcon,
      title: t("collection.dairect_banner.cards.wider_ecosystem"),
    },
  ];

  return (
    <section className="relative overflow-hidden rounded-[12px] p-4 sm:p-5 md:p-6">
      <img
        alt=""
        aria-hidden="true"
        className="absolute inset-0 h-full w-full object-cover"
        src={dairectBannerBackground}
      />
      <div className="relative flex flex-col gap-6 lg:flex-row lg:items-stretch lg:gap-6">
        <div className="flex w-full min-w-0 flex-col gap-6 md:gap-[30px] lg:max-w-[661px] lg:flex-[0_1_66%]">
          <div className="flex max-w-[558px] min-w-0 flex-col gap-2">
            <h2 className="text-[22px] font-medium leading-8 tracking-[-0.72px] text-takyon-black sm:text-[24px]">
              {t("collection.dairect_banner.title")}
            </h2>
            <p className="text-sm font-normal leading-6 tracking-[-0.32px] text-[rgba(9,17,18,0.6)] sm:text-base sm:leading-[22px]">
              {t("collection.dairect_banner.description_line_1")}
              <br />
              {t("collection.dairect_banner.description_line_2")}
              <br />
              {t("collection.dairect_banner.description_line_3")}
            </p>
          </div>

          <div
            className={cn(
              "grid gap-2 sm:grid-cols-2",
              isConstrained ? "2xl:grid-cols-3" : "xl:grid-cols-3",
            )}
          >
            {cards.map((card) => (
              <div
                className="flex min-h-[142px] min-w-0 flex-col gap-3 rounded-[8px] border border-[rgba(255,255,255,0.6)] bg-[rgba(255,255,255,0.1)] p-4 backdrop-blur-[25px]"
                key={card.title}
              >
                <div className="flex h-[50px] w-[50px] items-center justify-center rounded-[4px] border border-[rgba(255,255,255,0.6)] bg-[rgba(255,255,255,0.3)]">
                  <img
                    alt=""
                    aria-hidden="true"
                    className="h-6 w-6"
                    src={card.icon}
                  />
                </div>
                <p className="text-base font-medium leading-6 tracking-[-0.54px] text-takyon-black sm:text-lg">
                  {card.title}
                </p>
              </div>
            ))}
          </div>

          <div>
            <Button
              asChild
              className="h-auto w-full rounded-[8px] border-2 border-[rgba(9,17,18,0.2)] bg-takyon-black px-4 py-3 text-base font-medium leading-6 text-white shadow-[0px_0px_10px_0px_rgba(154,255,228,0.4)] hover:bg-takyon-black/90 sm:w-auto"
              variant="black"
            >
              <a href={DAIRECT_BANNER_URL} rel="noreferrer" target="_blank">
                {t("collection.dairect_banner.cta")}
                <img
                  alt=""
                  aria-hidden="true"
                  className="h-5 w-5"
                  src={dairectBannerArrow}
                />
              </a>
            </Button>
          </div>
        </div>

        <div className="relative hidden self-stretch lg:ml-auto lg:block lg:w-[31.5%] lg:min-w-[250px] lg:max-w-[347px]">
          <img
            alt=""
            aria-hidden="true"
            className="absolute inset-y-0 right-0 h-full w-full object-contain object-right"
            src={dairectBannerDecoration}
          />
        </div>
      </div>
    </section>
  );
}

export default function TaksTab({ collection }: { collection: iCollection }) {
  const { t } = useTranslation();
  const { setTitle } = useSectionTitle();
  const [tab, setTab] = useState("alltaks");
  const [isLoading, setIsLoading] = useState(false);
  const [graphData, setGraphData] = useState<MaskDataResponse>();
  const location = useLocation();
  const navigate = useNavigate();
  const { showModal } = useModal();
  const { state: sidebarState, isMobile } = useSidebar();

  const isSidebarExpandedDesktop = !isMobile && sidebarState === "expanded";
  const tabsHeaderClass = cn(
    "grid w-full gap-4 pb-5",
    isSidebarExpandedDesktop
      ? "2xl:grid-cols-[minmax(0,1fr)_auto]"
      : "xl:grid-cols-[minmax(0,1fr)_auto]",
  );
  const tabsListClass = cn(
    "h-auto w-full flex-wrap justify-start rounded-lg p-1 sm:w-fit",
    isSidebarExpandedDesktop
      ? "2xl:w-fit 2xl:justify-end"
      : "xl:w-fit xl:justify-end",
  );

  const loadMaskData = async (period?: number) => {
    try {
      setIsLoading(true);
      const datas = await getAnalysisMaskData({
        collectionId: collection._id,
        period: period,
      });
      // base data always available from the api
      setGraphData(datas);

      setIsLoading(false);
    } catch (error) {
      console.log("Error getting mask data", error);
    }
  };

  useEffect(() => {
    loadMaskData();
  }, []);

  useEffect(() => {
    setTitle(`${t("collection.tab_bookings")} - ${collection.name}`);
  }, [setTitle]);

  useEffect(() => {
    if (location.state?.openModal) {
      showModal(<CompletionMessage />, {
        title: "",
        description: "",
      });
      // Replace current location with same URL but without state
      navigate(location.pathname, { replace: true });
    }
  }, [location, showModal, navigate]);

  return isLoading ? (
    <div className="flex w-full gap-4">
      <div className="flex w-full flex-col gap-2">
        <Skeleton className="h-[286px] w-full" />
        <Skeleton className="h-[521px] w-full" />
      </div>
    </div>
  ) : (
    <div className="w-full max-w-full overflow-hidden">
      <div className="mb-6 mt-6 sm:mb-8 sm:mt-8">
        <DairectBanner isConstrained={isSidebarExpandedDesktop} />
      </div>
      <Separator className="my-8" />
      <p className="mb-6 text-xl font-semibold sm:mb-8">
        {t("collection.tab_bookings")}
      </p>
      {graphData && (
        <div className="mb-8 flex flex-col sm:mb-11">
          <Overview data={graphData.base as any} />
        </div>
      )}
      <Tabs defaultValue="alltaks" value={tab} onValueChange={setTab}>
        <div className={tabsHeaderClass}>
          <div className="flex min-w-0 flex-wrap items-center justify-start gap-1.5">
            {tab === "alltaks" && (
              <>
                <p className="min-w-0 break-words text-xl font-semibold">
                  {t("collection.tab_tak_title")}
                </p>
                <Popover>
                  <PopoverTrigger asChild>
                    <Info className="cursor-pointer" size={16} />
                  </PopoverTrigger>
                  <PopoverContent
                    align="start"
                    className="ml-0 w-[min(20rem,calc(100vw-2rem))]"
                  >
                    <p>{t("collection.tab_taks_description")}</p>
                  </PopoverContent>
                </Popover>
              </>
            )}
            {tab === "buybacks" && (
              <>
                <p className="min-w-0 break-words text-xl font-semibold">
                  {t("collection.tab_buybacks")}
                </p>
                <Popover>
                  <PopoverTrigger asChild>
                    <Info className="cursor-pointer" size={16} />
                  </PopoverTrigger>
                  <PopoverContent
                    align="start"
                    className="ml-0 w-[min(20rem,calc(100vw-2rem))]"
                  >
                    <p>{t("collection.tab_buybacks_description")}</p>
                  </PopoverContent>
                </Popover>
              </>
            )}
            {tab === "saleEvents" && (
              <>
                <p className="min-w-0 break-words text-xl font-semibold">
                  {t("collection.events")}
                </p>
              </>
            )}
          </div>
          <div className="flex w-full items-center gap-1 justify-start xl:justify-end">
            <TabsList className={tabsListClass}>
              <TabsTrigger
                className="min-h-9 flex-1 whitespace-normal px-3 py-2 text-center sm:flex-none"
                value="alltaks"
              >
                {t("collection.tab_bookings")}
              </TabsTrigger>
              <TabsTrigger
                className="min-h-9 flex-1 whitespace-normal px-3 py-2 text-center sm:flex-none"
                value="buybacks"
              >
                {t("collection.tab_buybacks")}
              </TabsTrigger>
              <TabsTrigger
                className="min-h-9 flex-1 whitespace-normal px-3 py-2 text-center sm:flex-none"
                value="saleEvents"
              >
                {t("collection.events")}
              </TabsTrigger>
            </TabsList>
          </div>
        </div>
        <TabsContent value="alltaks">
          <TaksDatatable collection={collection} />
        </TabsContent>
        <TabsContent value="buybacks">
          <BuybacksTab collection={collection} />
        </TabsContent>
        <TabsContent value="saleEvents">
          <SaleEventsTab collection={collection} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
